import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  Checkbox,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { startLoading, stopLoading } from '@/__core/redux/globalLoadingSlice';
import {
  InsertDriveFile,
  PictureAsPdf,
  Description,
  Image as ImageIcon,
  VideoFile,
  AudioFile,
  Archive,
  Download as DownloadIcon,
  Visibility,
} from '@mui/icons-material';
import { getBackendBaseUrl } from '@/setup/apiBaseUrl';

const isImageFile = (fileName: string, mimeType?: string): boolean => {
  if (mimeType?.startsWith('image/')) return true;
  const ext = fileName.split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'tiff'].includes(ext || '');
};

const getFileIcon = (fileName: string, mimeType?: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  if (mimeType?.startsWith('video/')) return <VideoFile sx={{ fontSize: 40, color: '#ff9800' }} />;
  if (mimeType?.startsWith('audio/')) return <AudioFile sx={{ fontSize: 40, color: '#9c27b0' }} />;
  if (mimeType?.includes('pdf')) return <PictureAsPdf sx={{ fontSize: 40, color: '#f44336' }} />;
  switch (extension) {
    case 'pdf': return <PictureAsPdf sx={{ fontSize: 40, color: '#f44336' }} />;
    case 'doc': case 'docx': return <Description sx={{ fontSize: 40, color: '#2196f3' }} />;
    case 'xls': case 'xlsx': return <Description sx={{ fontSize: 40, color: '#4caf50' }} />;
    case 'ppt': case 'pptx': return <Description sx={{ fontSize: 40, color: '#ff5722' }} />;
    case 'zip': case 'rar': case '7z': case 'tar': case 'gz': return <Archive sx={{ fontSize: 40, color: '#607d8b' }} />;
    case 'txt': case 'rtf': return <Description sx={{ fontSize: 40, color: '#757575' }} />;
    default: return <InsertDriveFile sx={{ fontSize: 40, color: '#9e9e9e' }} />;
  }
};

const resolveFileUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url) || /^data:/i.test(url)) return url;
  return `${getBackendBaseUrl()}${url}`;
};

interface DocumentListProps {
  documents: any[];
  viewMode: 'grid' | 'list';
  selectedFiles: string[];
  onFileSelect: (fileId: string) => void;
  onFileDownload?: (fileId: string) => void;
  onFileEdit?: (fileId: string) => void;
  onFilePreview?: (fileId: string) => void;
  authState: any;
  loading?: boolean;
}

const DocumentList: React.FC<DocumentListProps> = React.memo(({
  documents,
  viewMode,
  selectedFiles,
  onFileSelect,
  onFileDownload,
  onFileEdit,
  onFilePreview,
  authState,
  loading = false
}) => {
  const dispatch = useDispatch();

  // Handle loading state with GlobalLoadingBackdrop
  useEffect(() => {
    if (loading) {
      dispatch(startLoading({ message: 'Đang tải tài liệu...' }));
    } else {
      dispatch(stopLoading());
    }
  }, [loading, dispatch]);

  // Cleanup on unmount - ensure loading is always cleared
  useEffect(() => {
    return () => {
      dispatch(stopLoading());
    };
  }, [dispatch]);

  // Force clear loading when documents are empty and not loading
  useEffect(() => {
    if (!loading && documents.length === 0) {
      // Add a small delay to ensure any pending operations complete
      const timer = setTimeout(() => {
        dispatch(stopLoading());
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [loading, documents.length, dispatch]);

  // Force clear loading on mount to ensure clean state
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(stopLoading());
    }, 200);
    
    return () => clearTimeout(timer);
  }, [dispatch]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (documents.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          Không tìm thấy tài liệu nào
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Thử thay đổi bộ lọc hoặc tải lên file mới
        </Typography>
      </Box>
    );
  }

  if (viewMode === 'grid') {
    return (
      <Grid container spacing={1.5}>
        {documents.map((doc: any) => {
          const isImage = isImageFile(doc.name, doc.mime_type);
          const imgSrc = isImage ? resolveFileUrl(doc.file_url) : undefined;
          const selected = selectedFiles.includes(doc.id);

          return (
            <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }} key={doc.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selected ? 2 : 1,
                  borderColor: selected ? 'primary.main' : '#e5e7eb',
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: 'all 0.15s ease-in-out',
                  '&:hover': {
                    boxShadow: 3,
                    borderColor: 'primary.main',
                  },
                }}
                onClick={() => onFileSelect(doc.id)}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: '100%',
                    bgcolor: '#f9fafb',
                  }}
                >
                  {isImage && imgSrc ? (
                    <Box
                      component="img"
                      src={imgSrc}
                      alt={doc.name}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        p: 1,
                      }}
                    >
                      {getFileIcon(doc.name, doc.mime_type)}
                      <Typography
                        variant="caption"
                        sx={{
                          textAlign: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                          fontSize: '0.7rem',
                          color: '#6b7280',
                        }}
                      >
                        {doc.name}
                      </Typography>
                    </Box>
                  )}
                  <Checkbox
                    checked={selected}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      p: 0.5,
                      bgcolor: 'rgba(255,255,255,0.9)',
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onFileSelect(doc.id)}
                  />
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      display: 'flex',
                      justifyContent: 'center',
                        gap: 0.25,
                        py: 0.25,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.5))',
                      opacity: 0,
                      transition: 'opacity 0.2s ease-in-out',
                      '.MuiCard-root:hover &': { opacity: 1 },
                    }}
                  >
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isImage && imgSrc) {
                          window.open(imgSrc, '_blank');
                        } else {
                          onFilePreview?.(doc.id);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderRadius: 1,
                        bgcolor: 'rgba(255,255,255,0.9)',
                        color: '#374151',
                        width: 32,
                        height: 32,
                        '&:hover': { bgcolor: '#fff', color: '#3b82f6' },
                      }}
                    >
                      <Visibility sx={{ fontSize: 20 }} />
                    </Box>
                    <Box
                      onClick={() => onFileDownload?.(doc.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderRadius: 1,
                        bgcolor: 'rgba(255,255,255,0.9)',
                        color: '#374151',
                        width: 32,
                        height: 32,
                        '&:hover': { bgcolor: '#fff', color: '#22c55e' },
                      }}
                    >
                      <DownloadIcon sx={{ fontSize: 20 }} />
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  }

  // List view
  return (
    <Box>
      {documents.map((doc: any) => {
        const isImage = isImageFile(doc.name, doc.mime_type);
        const imgSrc = isImage ? resolveFileUrl(doc.file_url) : undefined;
        const selected = selectedFiles.includes(doc.id);

        return (
          <Box
            key={doc.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1,
              borderBottom: 1,
              borderColor: '#f1f5f9',
              cursor: 'pointer',
              bgcolor: selected ? '#eff6ff' : 'transparent',
              '&:hover': { bgcolor: '#f8fafc' },
              borderRadius: 1,
            }}
            onClick={() => onFileSelect(doc.id)}
          >
            <Checkbox
              checked={selected}
              size="small"
              onClick={(e) => e.stopPropagation()}
              onChange={() => onFileSelect(doc.id)}
            />

            {isImage && imgSrc ? (
              <Box
                component="img"
                src={imgSrc}
                alt={doc.name}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  objectFit: 'cover',
                  flexShrink: 0,
                  border: '1px solid #e5e7eb',
                }}
              />
            ) : (
              <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {getFileIcon(doc.name, doc.mime_type)}
              </Box>
            )}

            <Typography
              variant="body2"
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: selected ? 600 : 400,
              }}
            >
              {doc.name}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
});

DocumentList.displayName = 'DocumentList';

export default DocumentList;
